/**
 * Serve Finder Widget - Embeddable Bundle Entry Point
 *
 * Replaces MP's stock <mpp-opportunity-finder> on woodsidebible.org/serve/.
 * Public opportunities only, filtered in the browser from one cached
 * payload, cards that match the rest of the site, and a sign-up sheet that
 * opens right from the card. Renders inside a shadow root for style
 * isolation from the WordPress theme (see shell.tsx, shared with the
 * details bundle).
 *
 * Embed:
 *   <div id="serve-finder-root"></div>
 *   <script src="https://widgets.woodsidebible.org/serve-finder/widget.js"></script>
 *
 * Every stock widget attribute works as a data-* attribute or inside the
 * shared `data-params` string, plus the extras. See README.md.
 *
 *   <div id="serve-finder-root"
 *        data-params="campus=troy,ministry=116"
 *        data-details-url="https://woodsidebible.org/serve/"></div>
 */

import React from 'react';
import { ServeFinder } from '../src/components/ServeFinder';
import { attr, parseDataParams } from './params';
import { ShadowWidget } from './shell';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  detailsUrl?: string;
  showFullAddress?: boolean;
}

declare global {
  interface Window {
    SERVE_FINDER_WIDGET_CONFIG?: WidgetConfig;
    ServeFinderWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'serve-finder-root';

// data-* attributes that are the widget's own, not filter parameters.
const RESERVED = new Set([
  'params',
  'detailsUrl',
  'targeturl',
  'target',
  'showFullAddress',
  'showfulladdress',
]);

const truthy = (v: string | undefined) => !!v && /^(1|true|yes|on)$/i.test(v.trim());

function createFinderWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  return new ShadowWidget({
    containerIds: config.containerId ? [config.containerId] : [DEFAULT_CONTAINER_ID],
    overlayHostId: 'serve-finder-overlay',
    fontId: 'serve-finder-widget-montserrat',
    scrollLockFlag: '__SERVE_FINDER_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Serve Finder Widget',
    render: (container) => {
      // Precedence: explicit config object > dedicated data-* attributes >
      // the shared data-params string. Any other data-* attribute is a
      // filter parameter (data-ministryid="116", data-hide-filters="1").
      const embedParams = parseDataParams(container.getAttribute('data-params'));
      for (const [key, value] of Object.entries(container.dataset)) {
        if (RESERVED.has(key) || !value?.trim()) continue;
        embedParams.push([key, value.trim()]);
      }
      return (
        <ServeFinder
          apiBaseUrl={apiBaseUrl}
          embedParams={embedParams}
          detailsUrl={
            config.detailsUrl ??
            attr(container.dataset.detailsUrl) ??
            attr(container.dataset.targeturl) ??
            attr(container.dataset.target)
          }
          showFullAddress={
            config.showFullAddress ??
            (truthy(container.dataset.showFullAddress) || truthy(container.dataset.showfulladdress))
          }
        />
      );
    },
  });
}

// Auto-initialize
if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. (Do NOT derive it from the script origin:
  // the script is served from widgets.woodsidebible.org, which has no /api
  // routes.) window.SERVE_FINDER_WIDGET_CONFIG can override for local testing.
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.SERVE_FINDER_WIDGET_CONFIG || {}),
  };
  window.SERVE_FINDER_WIDGET_CONFIG = config;

  // Skip if already initialized (e.g. script loaded twice via bfcache)
  if (!window.ServeFinderWidget) {
    const boot = () => {
      const widget = createFinderWidget(config);
      widget.init();
      window.ServeFinderWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
